/**
 * Dial-logs timeline view-model: typed record shape, the ROUTER_ERR_* failure
 * decoder, and the pure roll-up helpers. Ported from the customer-connect
 * "App Activity" panel and adapted for the V8 console's single-entity scope
 * (no connector/application entities — headlines weave plain service/identity
 * names instead of clickable connector links).
 */

/** Field-name accessor for the raw ncfabriccircuits `_source` docs. Matches the
 *  `EventFields` shape injected via `EVENT_FIELDS` (see CLOUD_ZITI_EVENT_FIELDS). */
export interface DialLogEventFields {
    [key: string]: string;
    identityName: string;
    identityId: string;
    serviceName: string;
    serviceId: string;
}

/** Coarse "which side to investigate first" classification for a dial failure. */
export type DialFailureSide = 'host' | 'dialer' | 'config' | 'router' | 'unknown';

/** One run of headline text. `mono` flags a literal value (terminator address)
 *  rendered in a monospace face inline; other segments render as bold prose. */
export interface FailureHeadlineSegment {
    text: string;
    mono?: boolean;
}

export interface DialFailureHint {
    side: DialFailureSide;
    /** Plain-English summary, split into segments so the address can be mono. */
    headline: FailureHeadlineSegment[];
    /** Multi-sentence remediation hint rendered in the cell tooltip. */
    explanation: string;
}

/** A single dial event from the `ncfabriccircuits` index (hits-based). */
export interface DialLogRecord {
    timestampMs: number;
    timestampIso: string;
    eventType: 'created' | 'failed' | 'pathUpdated' | 'deleted' | string;
    /** True for created/pathUpdated, false for failed, null otherwise. */
    success: boolean | null;
    identityId?: string;
    identityName?: string;
    serviceId?: string;
    serviceName?: string;
    /** Host connector's identity id (`host_id`) — the identity serving the dial. */
    hostId?: string;
    hostName?: string;
    terminatorLocal?: string;
    terminatorRemote?: string;
    url?: string;
    port?: number;
    failureCause?: string;
    /** Ordered edge-router zitiIds the circuit traversed (ingress → transit → egress). */
    routerNodes?: string[];
    /** Number of fabric links in the path. */
    linkCount?: number;
    itemIndex: number;
    /** Populated by decorateRecord() on failed rows with a known code. */
    failureHint?: DialFailureHint | null;
}

/** A roll-up of dial events sharing the same group key (service or identity). */
export interface DialLogGroup {
    key: string;
    /** Group display name — service name (identity view) or identity name (service view). */
    primaryName: string | null;
    /** Id backing the primary name — serviceId (identity view) or identityId (service view). */
    primaryId: string | null;
    /** Host-side terminator address from the dial event (fallback for the address cell). */
    listenAddress: string | null;
    serviceId: string | null;
    serviceName: string | null;
    /** Client intercept address (`clientHostName:clientPortRange`), resolved from the service. */
    interceptAddress: string | null;
    /** Group-level dialer name — a single readable name, 'Multiple', or null. */
    dialerLabel: string | null;
    /** Group-level host name — a single readable name, 'Multiple', or null. */
    hostLabel: string | null;
    records: DialLogRecord[];
    count: number;
    successCount: number;
    failedCount: number;
    latestTimestampMs: number;
}

/**
 * A rollup of dials that share the same outcome signature, so a leaf of hundreds of
 * near-identical dials collapses to a handful of readable buckets. Failures group by
 * (error code, target address); successes — which carry no error code — group by target.
 */
export interface DialSignatureGroup {
    key: string;
    /** Representative event type, for the outcome chip (every dial in the bucket shares it). */
    eventType: DialLogRecord['eventType'];
    success: boolean | null;
    /** Failure code (`failureCause`); null for successes. */
    errorCode: string | null;
    /** Terminator the dial hit (`terminatorRemote`); often null on failures. */
    targetAddress: string | null;
    records: DialLogRecord[];
    count: number;
    failedCount: number;
    latestTimestampMs: number;
}

/**
 * Bucket a leaf's dials by outcome signature: event type + error code + target address.
 * Failures split by why they failed (and where, when a target is present); successes fall
 * together by target. Failed buckets sort first (most actionable), then by size, then recency.
 * Records keep their incoming order (newest first) within each bucket.
 */
export function buildSignatureGroups(records: DialLogRecord[]): DialSignatureGroup[] {
    const map = new Map<string, DialSignatureGroup>();
    for (const r of records) {
        const errorCode = r.failureCause ?? null;
        const targetAddress = r.terminatorRemote ?? null;
        const key = `${r.eventType}|${errorCode ?? ''}|${targetAddress ?? ''}`;
        let sig = map.get(key);
        if (!sig) {
            sig = {
                key,
                eventType: r.eventType,
                success: r.success,
                errorCode,
                targetAddress,
                records: [],
                count: 0,
                failedCount: 0,
                latestTimestampMs: 0,
            };
            map.set(key, sig);
        }
        sig.records.push(r);
        sig.count++;
        if (r.success === false) sig.failedCount++;
        if (r.timestampMs > sig.latestTimestampMs) sig.latestTimestampMs = r.timestampMs;
    }
    return [...map.values()].sort((a, b) => {
        const af = a.success === false ? 0 : 1;
        const bf = b.success === false ? 0 : 1;
        if (af !== bf) return af - bf;
        if (b.count !== a.count) return b.count - a.count;
        return b.latestTimestampMs - a.latestTimestampMs;
    });
}

/** Collapse a set of readable names into a single label: the name if unique,
 *  'Multiple' if several, or null if none. */
export function collapseNames(names: Array<string | null>): string | null {
    const distinct = new Set(names.filter((n): n is string => !!n));
    if (distinct.size === 0) return null;
    if (distinct.size === 1) return [...distinct][0];
    return 'Multiple';
}

export type GroupSortColumn = 'name' | 'listen' | 'status' | 'lastDial';
export type GroupSortDir = 'asc' | 'desc';

function stringOrUndef(v: unknown): string | undefined {
    return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function numericPort(v: unknown): number | undefined {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.length > 0) {
        const n = Number(v);
        return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
}

/** Display an entity name exactly as stored — trimmed, with every segment kept.
 *  We deliberately do NOT drop parts of the name (e.g. Ziti's `name|id|id` form):
 *  that layout is a per-network convention, not a guarantee, so stripping it would
 *  lose fidelity. Length is handled in the UI with truncation + tooltips. */
export function displayEntityName(s: string | null | undefined): string | null {
    const trimmed = s?.trim();
    return trimmed ? trimmed : null;
}

/** Pick the terminator_remote_addr representation (top-level, then nested under
 *  `path`, then a separate ip/port pair). */
function resolveTerminatorRemote(src: Record<string, unknown>, path: Record<string, unknown> | null): string | undefined {
    const top = stringOrUndef(src['terminator_remote_addr']);
    if (top) return top;
    const nested = path ? stringOrUndef(path['terminator_remote_addr']) : undefined;
    if (nested) return nested;
    const ip = stringOrUndef(src['terminator_remote_ip']);
    const port = stringOrUndef(src['terminator_remote_port']);
    if (ip && port) return `${ip}:${port}`;
    if (ip) return ip;
    return undefined;
}

/** Split an `ip:port` terminator address into host + numeric port. */
export function parseTerminatorRemote(addr: string | undefined): { url?: string; port?: number } {
    if (!addr) return {};
    const idx = addr.lastIndexOf(':');
    if (idx <= 0 || idx === addr.length - 1) return { url: addr };
    const host = addr.slice(0, idx);
    const portNum = Number(addr.slice(idx + 1));
    return Number.isFinite(portNum) ? { url: host, port: portNum } : { url: addr };
}

/** Ordered edge-router zitiIds the circuit traversed (`path.nodes`), if present. */
function extractRouterNodes(path: Record<string, unknown> | null): string[] | undefined {
    const nodes = path?.['nodes'];
    if (!Array.isArray(nodes)) return undefined;
    const ids = nodes.filter((n): n is string => typeof n === 'string' && n.length > 0);
    return ids.length ? ids : undefined;
}

function terminatorLocal(src: Record<string, unknown>, path: Record<string, unknown> | null): string | undefined {
    return stringOrUndef(src['terminator_local_addr']) ?? (path ? stringOrUndef(path['terminator_local_addr']) : undefined);
}

function deriveSuccess(eventType: string): boolean | null {
    if (eventType === 'failed') return false;
    if (eventType === 'created' || eventType === 'pathUpdated') return true;
    return null;
}

/**
 * Walk the raw `_source` docs (from `ElasticsearchService.hitsToArray`) into
 * typed `DialLogRecord`s. Names are read via the injected field map (with
 * defensive fallbacks to the raw ncfabriccircuits field names). Records missing
 * a usable timestamp or event type are dropped.
 */
export function toDialLogRecords(rawSources: any[], fields: DialLogEventFields): DialLogRecord[] {
    const records: DialLogRecord[] = [];
    if (!Array.isArray(rawSources)) return records;
    let index = 0;
    for (const src of rawSources) {
        if (!src || typeof src !== 'object') continue;
        const tsRaw = src['@timestamp'];
        const eventType = src['event_type'];
        if (typeof tsRaw !== 'string' || typeof eventType !== 'string') continue;
        const tsMs = Date.parse(tsRaw);
        if (Number.isNaN(tsMs)) continue;

        const path = src['path'] && typeof src['path'] === 'object' ? (src['path'] as Record<string, unknown>) : null;
        const terminatorRemote = resolveTerminatorRemote(src, path);
        const { url: parsedUrl, port: parsedPort } = parseTerminatorRemote(terminatorRemote);

        records.push({
            timestampMs: tsMs,
            timestampIso: tsRaw,
            eventType,
            success: deriveSuccess(eventType),
            identityName:
                stringOrUndef(src[fields.identityName]) ?? stringOrUndef(src['identity_name']) ?? stringOrUndef(src['identity_name.keyword']),
            identityId:
                stringOrUndef(src[fields.identityId]) ?? stringOrUndef(src['identity_id']) ?? stringOrUndef(src['nf_endpoint_id']),
            serviceName: stringOrUndef(src[fields.serviceName]) ?? stringOrUndef(src['service_name']),
            serviceId:
                stringOrUndef(src[fields.serviceId]) ?? stringOrUndef(src['service_id']) ?? stringOrUndef(src['nf_service_id']),
            hostId: stringOrUndef(src['host_id']) ?? stringOrUndef(src['_tags']?.['hostId']),
            hostName: stringOrUndef(src['host_name']) ?? stringOrUndef(src['nf_host_name']),
            terminatorLocal: terminatorLocal(src, path),
            terminatorRemote,
            url: parsedUrl,
            port: parsedPort ?? numericPort(src['terminator_remote_port']),
            failureCause: stringOrUndef(src['failure_cause']),
            routerNodes: extractRouterNodes(path),
            linkCount: numericPort(src['link_count']),
            itemIndex: index++,
        });
    }
    return records;
}

/** Format a service's client-side intercept address as `host:port`, collapsing
 *  single-value port ranges (`3389-3389` → `3389`). Null when there's no host. */
export function formatInterceptAddress(clientHostName: string | null | undefined, clientPortRange: string | null | undefined): string | null {
    const host = clientHostName?.trim();
    if (!host) return null;
    const range = clientPortRange?.trim();
    if (!range) return host;
    const [low, high] = range.split('-').map((p) => p.trim());
    const port = high && high !== low ? `${low}-${high}` : low;
    return port ? `${host}:${port}` : host;
}

/** Group key — by service in the identity (endpoint) view, by identity in the
 *  service view. Falls back to name, then a host/event composite. */
export function dialGroupKey(r: DialLogRecord, isEndpointView: boolean): string {
    if (isEndpointView) {
        if (r.serviceId) return `svc:${r.serviceId}`;
        if (r.serviceName) return `svcname:${r.serviceName}`;
        return `svc:host:${r.hostName ?? ''}|${r.eventType}`;
    }
    if (r.identityId) return `id:${r.identityId}`;
    if (r.identityName) return `idname:${r.identityName}`;
    return `id:host:${r.hostName ?? ''}|${r.eventType}`;
}

/** Group display name for the current view. */
export function dialGroupLabel(r: DialLogRecord, isEndpointView: boolean): string | null {
    return isEndpointView ? displayEntityName(r.serviceName) : displayEntityName(r.identityName);
}

/** Context the headline builder interpolates the relevant entity name from. */
export interface HeadlineContext {
    /** Host/target side — the service being dialed (display name). */
    hostName: string | null;
    /** Dialer side — the identity initiating the dial (display name). */
    dialerName: string | null;
    serviceName: string | null;
    /** Forwarding address (`terminator_remote_addr`), surfaced inline as mono. */
    terminator: string | null;
}

interface FailureHintTemplate {
    side: DialFailureSide;
    buildHeadline: (ctx: HeadlineContext) => FailureHeadlineSegment[];
    explanation: string;
}

/**
 * Router-layer failure-code → human translation map. Headlines weave the
 * relevant entity name directly into the sentence so the operator reads the
 * actor in the message itself. Codes are the literal `failure_cause` strings the
 * `ncfabriccircuits` index records; unknown codes fall through to a generic hint.
 */
const FAILURE_HINT_TEMPLATES_BY_CODE: Record<string, FailureHintTemplate> = {
    ROUTER_ERR_DIAL_TIMED_OUT: {
        side: 'host',
        buildHeadline: (ctx) => {
            if (ctx.hostName && ctx.terminator) {
                return [{ text: ctx.hostName }, { text: ' couldn\'t reach ' }, { text: ctx.terminator, mono: true }, { text: ' in time' }];
            }
            if (ctx.hostName) {
                return [{ text: ctx.hostName }, { text: ' couldn\'t reach its forwarding address in time' }];
            }
            return [{ text: 'The host couldn\'t reach its forwarding address in time' }];
        },
        explanation:
            'The router reached the host but its forward to the configured address timed out. Verify the service at that address is running, that it\'s reachable from the host site\'s network, and that no firewall along the path is dropping the connection.',
    },
    ROUTER_ERR_CONN_REFUSED: {
        side: 'host',
        buildHeadline: (ctx) => {
            if (ctx.hostName && ctx.terminator) {
                return [{ text: ctx.terminator, mono: true }, { text: ' refused the connection from ' }, { text: ctx.hostName }];
            }
            if (ctx.hostName) {
                return [{ text: ctx.hostName }, { text: '\'s forwarding address refused the connection' }];
            }
            return [{ text: 'The forwarding address refused the connection' }];
        },
        explanation:
            'The service at the host\'s forwarding address actively rejected the connection — typically the service isn\'t listening on the expected port, or its process isn\'t running. Confirm the service is up at the shown address.',
    },
    ROUTER_ERR_REJECTED_BY_APPLICATION: {
        side: 'host',
        buildHeadline: (ctx) => {
            if (ctx.hostName && ctx.terminator) {
                return [{ text: ctx.hostName }, { text: '\'s target service at ' }, { text: ctx.terminator, mono: true }, { text: ' rejected the connection' }];
            }
            if (ctx.hostName) {
                return [{ text: ctx.hostName }, { text: '\'s target service rejected the connection' }];
            }
            return [{ text: 'The target service rejected the connection' }];
        },
        explanation:
            'The dial reached the target service and a TCP connection was established, but the application protocol layer rejected it. Common causes: authentication or authorization failure at the app, protocol/version mismatch on the port, a failed TLS handshake, or an app-level access policy denying the request. The Ziti network side is healthy — check the target service\'s own logs at the host site.',
    },
    NO_HOSTS: {
        side: 'config',
        buildHeadline: (ctx) =>
            ctx.serviceName
                ? [{ text: `No host connector online to serve ${ctx.serviceName}` }]
                : [{ text: 'No host connector available for this service' }],
        explanation:
            'The service has no host connector currently online and eligible to serve the dialer. Bring a host connector online for this service, or check that the access policy routes this dialer to an existing host.',
    },
    ROUTER_ERR_DIAL_FAILED: {
        side: 'host',
        buildHeadline: (ctx) => {
            if (ctx.hostName && ctx.terminator) {
                return [{ text: ctx.hostName }, { text: ' couldn\'t complete its forward to ' }, { text: ctx.terminator, mono: true }];
            }
            if (ctx.hostName) {
                return [{ text: ctx.hostName }, { text: ' couldn\'t complete the forward' }];
            }
            return [{ text: 'The host couldn\'t complete the forward' }];
        },
        explanation:
            'Generic forward failure on the host side — the host accepted the dial but couldn\'t reach its forwarding address. Inspect the host connector\'s logs around the timestamp for the underlying error.',
    },
    NO_TERMINATORS: {
        side: 'config',
        buildHeadline: (ctx) =>
            ctx.serviceName
                ? [{ text: `No host connector is currently serving ${ctx.serviceName}` }]
                : [{ text: 'No host connector is currently serving this service' }],
        explanation:
            'The router couldn\'t find an active host registration (terminator) for this service. Common causes: the host connector is offline, it hasn\'t re-published its terminator since its last restart, or no host connector is configured to serve the service. Confirm at least one host connector is online and bound to it.',
    },
    NO_ONLINE_TERMINATORS: {
        side: 'config',
        buildHeadline: (ctx) =>
            ctx.serviceName
                ? [{ text: `No online host connector to serve ${ctx.serviceName}` }]
                : [{ text: 'No online host connector for this service' }],
        explanation:
            'The service has a registered host terminator, but none are online right now — so there\'s no live host to route the dial to. The host connector may be offline, restarting, or reconnected without re-publishing its terminator; confirm the host connector is online.',
    },
    ROUTER_ERR_DIAL_CANCELLED: {
        side: 'dialer',
        buildHeadline: (ctx) =>
            ctx.dialerName ? [{ text: ctx.dialerName }, { text: ' cancelled the dial' }] : [{ text: 'The client cancelled the dial' }],
        explanation:
            'The dialer aborted before the dial completed — usually the client process disconnected, or the dialer connector restarted mid-dial. Recurring cancellations may indicate an unstable dialer site or aggressive client timeouts.',
    },
    ROUTER_RESPONSE_TIMEOUT: {
        side: 'host',
        buildHeadline: (ctx) =>
            ctx.hostName
                ? [{ text: ctx.hostName }, { text: ' didn\'t respond before the dial timed out' }]
                : [{ text: 'No host responded before the dial timed out' }],
        explanation:
            'A route was set up but the terminating (host) side never answered within the timeout. Usually the chosen host connector is offline, overloaded, or unreachable — or an over-broad access-policy bind routed the dial to a connector that doesn\'t actually host this service. Confirm a healthy host, and that its bind scope targets only connectors that serve it.',
    },
    NO_PATH: {
        side: 'router',
        buildHeadline: (ctx) =>
            ctx.serviceName
                ? [{ text: `No network path to a host serving ${ctx.serviceName}` }]
                : [{ text: 'No network path to a host for this service' }],
        explanation:
            'No route could be established from the dialer to any host serving this service. Typically every host connector is offline or unreachable, no host is bound to the service, or a link between the sites is down. Bring a host online (or fix its binding) and verify connectivity between the dialer and host sites.',
    },
};

const UNKNOWN_FAILURE_HINT_TEMPLATE: FailureHintTemplate = {
    side: 'unknown',
    buildHeadline: () => [{ text: 'Dial failed' }],
    explanation:
        'The router reported a failure without a recognized code. Cross-reference the raw code with the host and dialer connector logs around the timestamp for context.',
};

/**
 * Decode a raw `failure_cause` string into a human hint. Tolerates both the
 * `ROUTER_ERR_` prefixed router codes and the bare terminator codes (NO_HOSTS,
 * NO_TERMINATORS) by trying the raw code, then stripping/adding the prefix.
 * Falls back to a generic hint so every failed row still gets a headline.
 */
export function decodeFailureHint(cause: string | undefined, ctx: HeadlineContext): DialFailureHint | null {
    if (!cause) return null;
    const template =
        FAILURE_HINT_TEMPLATES_BY_CODE[cause] ??
        (cause.startsWith('ROUTER_ERR_')
            ? FAILURE_HINT_TEMPLATES_BY_CODE[cause.slice('ROUTER_ERR_'.length)]
            : FAILURE_HINT_TEMPLATES_BY_CODE[`ROUTER_ERR_${cause}`]) ??
        UNKNOWN_FAILURE_HINT_TEMPLATE;
    return {
        side: template.side,
        headline: template.buildHeadline(ctx),
        explanation: template.explanation,
    };
}

/**
 * Build the headline context for a record. Entity names are intentionally left
 * out of the prose headline — in this data they're pipe-delimited, UUID-bearing
 * strings that wreck a sentence — so headlines use their clean, entity-free form
 * and identity is carried faithfully by the detail grid instead. Only the short
 * terminator address (e.g. `10.0.0.5:5432`) is woven in, since it reads cleanly.
 */
export function buildHeadlineContext(r: DialLogRecord): HeadlineContext {
    return {
        hostName: null,
        dialerName: null,
        serviceName: null,
        terminator: r.terminatorRemote && r.terminatorRemote.length > 0 ? r.terminatorRemote : null,
    };
}

/** Attach the decoded failure hint to failed records (leaves others untouched). */
export function decorateRecord(r: DialLogRecord): DialLogRecord {
    return {
        ...r,
        failureHint: r.success === false ? decodeFailureHint(r.failureCause, buildHeadlineContext(r)) : null,
    };
}

/** One time bucket of dial activity for the historical chart. */
export interface DialTimeBucket {
    startMs: number;
    endMs: number;
    success: number;
    failed: number;
    total: number;
}

/**
 * Bucket records into a fixed number of equal time slots spanning [startMs, endMs).
 * Empty slots stay zero so gaps render. Records outside the range are clamped into
 * the edge buckets. `success`/`failed` follow the record's `success` tri-state;
 * `total` counts every record in the slot (including null-outcome events).
 */
export function buildTimeBuckets(records: DialLogRecord[], startMs: number, endMs: number, bucketCount: number): DialTimeBucket[] {
    const count = Math.max(1, Math.floor(bucketCount));
    const span = Math.max(1, endMs - startMs);
    const bucketMs = span / count;
    const buckets: DialTimeBucket[] = Array.from({ length: count }, (_, i) => ({
        startMs: startMs + i * bucketMs,
        endMs: startMs + (i + 1) * bucketMs,
        success: 0,
        failed: 0,
        total: 0,
    }));
    for (const r of records) {
        let idx = Math.floor((r.timestampMs - startMs) / bucketMs);
        if (idx < 0) idx = 0;
        if (idx >= count) idx = count - 1;
        const b = buckets[idx];
        b.total++;
        if (r.success === false) b.failed++;
        else if (r.success === true) b.success++;
    }
    return buckets;
}

/** Round to 2 decimals for compact SVG path strings. */
function svgNum(n: number): number {
    return Math.round(n * 100) / 100;
}

/**
 * Smooth line path (monotone cubic Hermite, Fritsch–Carlson) through evenly-spaced
 * values scaled into a width×height box with y inverted. `topInset` leaves headroom
 * (fraction of height) so peaks don't kiss the top. Monotone → a count line never
 * overshoots below the baseline. Empty string when there's no data.
 */
export function buildLinePath(data: number[], width: number, height: number, max: number, topInset = 0.08): string {
    if (!data || data.length === 0 || max <= 0) return '';
    const top = height * topInset;
    const usable = height - top;
    const n = data.length;
    const px = (i: number) => (i / Math.max(1, n - 1)) * width;
    const py = (v: number) => height - (Math.max(0, v) / max) * usable;
    if (n === 1) return `M 0,${svgNum(py(data[0]))} L ${svgNum(width)},${svgNum(py(data[0]))}`;

    const xs = data.map((_, i) => px(i));
    const ys = data.map((v) => py(v));
    const dx: number[] = [];
    const slope: number[] = [];
    for (let i = 0; i < n - 1; i++) {
        dx[i] = xs[i + 1] - xs[i];
        slope[i] = (ys[i + 1] - ys[i]) / dx[i];
    }
    const m: number[] = [slope[0]];
    for (let i = 1; i < n - 1; i++) {
        m[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
    }
    m[n - 1] = slope[n - 2];
    // Fritsch–Carlson: keep the interpolant monotone.
    for (let i = 0; i < n - 1; i++) {
        if (slope[i] === 0) {
            m[i] = 0;
            m[i + 1] = 0;
            continue;
        }
        const a = m[i] / slope[i];
        const b = m[i + 1] / slope[i];
        const h = Math.hypot(a, b);
        if (h > 3) {
            const t = 3 / h;
            m[i] = t * a * slope[i];
            m[i + 1] = t * b * slope[i];
        }
    }
    let d = `M ${svgNum(xs[0])},${svgNum(ys[0])}`;
    for (let i = 0; i < n - 1; i++) {
        const c1x = xs[i] + dx[i] / 3;
        const c1y = ys[i] + (m[i] * dx[i]) / 3;
        const c2x = xs[i + 1] - dx[i] / 3;
        const c2y = ys[i + 1] - (m[i + 1] * dx[i]) / 3;
        d += ` C ${svgNum(c1x)},${svgNum(c1y)} ${svgNum(c2x)},${svgNum(c2y)} ${svgNum(xs[i + 1])},${svgNum(ys[i + 1])}`;
    }
    return d;
}

/** Close a line path down to the baseline to make a filled area. */
export function buildAreaPath(linePath: string, width: number, height: number): string {
    if (!linePath) return '';
    return `${linePath} L ${svgNum(width)},${svgNum(height)} L 0,${svgNum(height)} Z`;
}

/** Round a max value up to a clean axis bound (1 / 2 / 2.5 / 5 / 10 × 10^k). */
export function niceMax(value: number): number {
    if (value <= 0) return 0;
    const pow = Math.pow(10, Math.floor(Math.log10(value)));
    const n = value / pow;
    const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
    return step * pow;
}
